<template>
    <ExportWrapper :file-name="fileName">
        <QueryLoaderGuard
            :is-pending="isPending"
            :is-error="isError"
        >
            <template v-if="generatorId">
                <ExportViewReportTitleSection
                    :report-number="reportNumber"
                    :report-type="reportTypeAsNumber"
                    :generator-id="generatorId"
                />

                <div v-if="generatorId" class="master-data-wrapper">
                    <MasterDataPanel 
                      :generator-id="generatorId"
                      :show-more-default="masterDataShowMoreDefault"
                      is-export-layout
                    />
                </div>

                <div class="master-data-wrapper">
                    <ExportViewReportVisualInspectionSection
                        v-if="reportTypeAsNumber === ReportType.GeneratorStatusReport && visualInspectionData"
                        :status="data?.visualInspectionReport?.status"
                        :data="visualInspectionData"
                    />
                </div>

                <div v-if="generatorId" class="master-data-wrapper">
                  <ViewGeneratorReportMeasurementsSection
                      :report-number="reportNumber"
                      :generator-id="generatorId"
                      is-export-layout
                  />
                </div>

                <ExportFooterSection />
            </template>
        </QueryLoaderGuard>
    </ExportWrapper>
</template>

<script setup lang="ts">
import ExportWrapper from '@/pages/exports/ExportWrapper.vue'
import ExportViewReportTitleSection from '@/pages/exports/reports/view-report/generators/sections/ExportViewReportTitleSection.vue'
import MasterDataPanel from '@/pages/generators/condition-status/sections/master-data/MasterDataPanel.vue'
import { computed, toRefs } from 'vue'
import { ReportType, ReportTypes } from '@/configuration/report/report-types'
import QueryLoaderGuard from '@/components/guards/QueryLoaderGuard.vue'
import ExportFooterSection from '@/pages/exports/common/ExportFooterSection.vue'
import { format } from 'date-fns'
import { DATE_FORMAT } from '@/utils/date-utils'
import { useI18n } from 'vue-i18n'
import { useGeneratorDetailsQuery } from '@/api/queries/generators/useGeneratorDetailsQuery'
import ViewGeneratorReportMeasurementsSection from '@/pages/reports/view-report-page/generator/sections/ViewGeneratorReportMeasurementsSection.vue'
import { useGeneratorsReportDetailsExportStateStore } from '@/store/reports/export/generators/exportReportStateStore'
import { useGeneratorReportGetQuery } from '@/api/queries/reports/useGeneratorReportGetQuery'
import ExportViewReportVisualInspectionSection from '@/pages/exports/reports/view-report/generators/sections/ExportViewReportVisualInspectionSection.vue'

const props = defineProps<{
    generatorId: string
    reportNumber: string
    reportType: string
}>()

const { generatorId, reportNumber, reportType } = toRefs(props)

const { t } = useI18n()
const generatorsReportDetailsExportStateStore = useGeneratorsReportDetailsExportStateStore()

const masterDataShowMoreDefault = computed(() => generatorsReportDetailsExportStateStore.reportDetailsExportParameters.masterDataShowMore === 'true')

const reportTypeAsNumber = computed(() => {
    const parsedNumber = parseInt(reportType.value)

    return isNaN(parsedNumber) ? ReportType.OilAnalysis : parsedNumber
})

const { data, isPending, isError } = useGeneratorReportGetQuery(reportNumber, generatorId)

const visualInspectionData = computed(() => {
    if (!data.value?.visualInspectionReport) {
        return undefined
    }

    return data.value.visualInspectionReport
})

const { data: generatorDetailsData } = useGeneratorDetailsQuery(generatorId)

const fileName = computed(() => {
    if (!generatorDetailsData.value) {
        return undefined
    }

    const plantGroupName = generatorDetailsData.value.riverGroupName
    const plantName = generatorDetailsData.value.powerPlantName
    const generatorSid = generatorDetailsData.value.generatorID
    const reportType = t(ReportTypes.find((e) => e.value === reportTypeAsNumber.value)?.labelKey ?? '-').split(' ').join('')
    const currentDate = format(new Date(), DATE_FORMAT)

    return `HCA_${plantGroupName}_${plantName}_${generatorSid}_${reportType}_${reportNumber.value}_${currentDate}`
})
</script>

<style scoped lang="scss">
:deep(.content-panel) {
    border: 1px solid $pbl-divider;
    box-shadow: unset;
}

.master-data-wrapper {
    padding: $spacing-xs $spacing-s;
    break-inside: avoid;
}

.section-wrapper {
    padding: $spacing-xs $spacing-s;
}
</style>
